.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--badge-gap, var(--spacing-xs));
  border: 1px solid transparent;
  border-radius: var(--badge-radius);
  padding: var(--badge-py) var(--badge-px);
  font-family: var(--font-family-base);
  font-weight: var(--font-weight-medium);
  font-size: var(--badge-font-size);
  line-height: var(--badge-line-height);
  white-space: nowrap;
  vertical-align: middle;
  /* Never wider than its container: a long value (a device GUID in a 320px card) is cut to an
     ellipsis below instead of running past the card (WCAG 1.4.10). The percentage does not count
     toward intrinsic sizing, so an auto-width table cell still sizes to the whole badge. */
  max-width: 100%;
}

/* Sizes ------------------------------------------------------------------- */
.sm {
  --badge-py: var(--spacing-xxs);
  --badge-px: var(--spacing-sm);
  --badge-radius: 0.375rem;
  --badge-font-size: var(--text-xs-font-size);
  --badge-line-height: var(--text-xs-line-height);
  --badge-dot-size: 0.375rem;
  --badge-icon-pad: 0.25rem;
}
.md {
  --badge-py: var(--spacing-xxs);
  --badge-px: var(--spacing-md);
  --badge-radius: 0.375rem;
  --badge-font-size: var(--text-sm-font-size);
  --badge-line-height: var(--text-sm-line-height);
  --badge-dot-size: 0.5rem;
  --badge-icon-pad: 0.3125rem;
}
.lg {
  --badge-py: var(--spacing-xs);
  --badge-px: 0.625rem;
  --badge-radius: 0.5rem;
  --badge-font-size: var(--text-sm-font-size);
  --badge-line-height: var(--text-sm-line-height);
  --badge-dot-size: 0.5rem;
  --badge-icon-pad: 0.4375rem;
}

/* Color variant — soft filled surface -------------------------------------- */
.color {
  background: var(--badge-bg);
  border-color: var(--badge-border);
  color: var(--badge-fg);
}

/* Modern variant — neutral surface; accent (dot/icon) still follows `color`. */
.modern {
  background: var(--surface);
  border-color: var(--border);
  color: var(--gray-chip-fg);
  box-shadow: var(--shadow-xs);
}

/* Per-color tokens: surface, border, text, and accent (dot / close glyph). */
.gray {
  --badge-bg: var(--gray-chip-bg);
  --badge-border: var(--gray-chip-border);
  --badge-fg: var(--gray-chip-fg);
  --badge-accent: var(--gray-chip-accent);
  --badge-close: var(--gray-chip-close);
}
.brand {
  --badge-bg: var(--brand-chip-bg);
  --badge-border: var(--brand-chip-border);
  --badge-fg: var(--brand-chip-fg);
  --badge-accent: var(--brand-chip-accent);
  --badge-close: var(--brand-chip-close);
}
.error {
  --badge-bg: var(--error-chip-bg);
  --badge-border: var(--error-chip-border);
  --badge-fg: var(--error-chip-fg);
  --badge-accent: var(--error-chip-accent);
  --badge-close: var(--error-chip-close);
}
.warning {
  --badge-bg: var(--warning-chip-bg);
  --badge-border: var(--warning-chip-border);
  --badge-fg: var(--warning-chip-fg);
  --badge-accent: var(--warning-chip-accent);
  --badge-close: var(--warning-chip-close);
}
.success {
  --badge-bg: var(--success-chip-bg);
  --badge-border: var(--success-chip-border);
  --badge-fg: var(--success-chip-fg);
  --badge-accent: var(--success-chip-accent);
  --badge-close: var(--success-chip-close);
}
.blue {
  --badge-bg: var(--blue-chip-bg);
  --badge-border: var(--blue-chip-border);
  --badge-fg: var(--blue-chip-fg);
  --badge-accent: var(--blue-chip-accent);
  --badge-close: var(--blue-chip-close);
}
.purple {
  --badge-bg: var(--purple-chip-bg);
  --badge-border: var(--purple-chip-border);
  --badge-fg: var(--purple-chip-fg);
  --badge-accent: var(--purple-chip-accent);
  --badge-close: var(--purple-chip-close);
}
.cyan {
  --badge-bg: var(--cyan-chip-bg);
  --badge-border: var(--cyan-chip-border);
  --badge-fg: var(--cyan-chip-fg);
  --badge-accent: var(--cyan-chip-accent);
  --badge-close: var(--cyan-chip-close);
}
.yellow {
  --badge-bg: var(--yellow-chip-bg);
  --badge-border: var(--yellow-chip-border);
  --badge-fg: var(--yellow-chip-fg);
  --badge-accent: var(--yellow-chip-accent);
  --badge-close: var(--yellow-chip-close);
}
/* The palette's `orange-dark` scale — the only orange it carries (HS-1442). */
.orange {
  --badge-bg: var(--orange-dark-chip-bg);
  --badge-border: var(--orange-dark-chip-border);
  --badge-fg: var(--orange-dark-chip-fg);
  --badge-accent: var(--orange-dark-chip-accent);
  --badge-close: var(--orange-dark-chip-close);
}

/* Slot adjustments — trim the padding on the side that carries an affordance. */
.with-dot {
  --badge-gap: var(--spacing-sm);
}
.has-leading {
  padding-inline-start: calc(var(--badge-px) - 0.125rem);
}
.has-trailing {
  padding-inline-end: calc(var(--badge-px) - 0.125rem);
}
.has-close {
  padding-inline-end: calc(var(--badge-px) - 0.25rem);
}

.icon-only {
  padding: var(--badge-icon-pad);
}

/* Parts ------------------------------------------------------------------- */
.dot {
  flex-shrink: 0;
  width: var(--badge-dot-size);
  height: var(--badge-dot-size);
  border-radius: 50%;
  background: var(--badge-accent);
}

.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.75rem;
  line-height: 0;
}
.icon > svg {
  width: 1em;
  height: 1em;
}

.text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0.125rem;
  border: 0;
  border-radius: 0.1875rem;
  background: transparent;
  color: var(--badge-close, currentColor);
  font-size: 0.75rem;
  line-height: 0;
  cursor: pointer;
}
.close:hover {
  color: var(--badge-fg, currentColor);
}
.close:focus-visible {
  outline: 2px solid transparent;
  outline-offset: 1px;
  box-shadow: var(--focus-ring);
}
